import { isTradeable } from '../../utils/price';
import { createSlice } from '@reduxjs/toolkit';
import axios from '../../axios';

// How long a fetched stock list is reused before another page/effect refetches it.
const MAX_AGE_MS = 30000;

// The stock list endpoint is slow (several seconds), so the last list is kept in
// localStorage and shown straight away on the next visit while a fresh one loads.
const CACHE_KEY = 'stockListCache';

function readCachedList() {
	try {
		const parsed = JSON.parse(localStorage.getItem(CACHE_KEY));
		return Array.isArray(parsed) ? parsed : [];
	} catch (e) {
		return [];
	}
}

function writeCachedList(list) {
	try {
		localStorage.setItem(CACHE_KEY, JSON.stringify(list));
	} catch (e) {
		// Storage full or blocked (private mode): the in-memory list still works.
	}
}

const stocksSlice = createSlice({
	name: 'stocks',
	initialState: {
		// lastFetched stays null so the cached list is always refreshed on load.
		list: readCachedList(),
		loading: false,
		error: null,
		lastFetched: null
	},
	reducers: {
		stocksRequested(state) {
			state.loading = true;
			state.error = null;
		},
		stocksReceived(state, action) {
			// Only stocks that can be traded are listed anywhere in the app
			state.list = (action.payload || []).filter(isTradeable);
			state.loading = false;
			state.lastFetched = Date.now();
		},
		stocksFailed(state, action) {
			state.loading = false;
			state.error = action.payload || 'Failed to load stocks';
		},
		setInWatchlist(state, action) {
			const { symbol, value } = action.payload;
			const item = state.list.find(s => s.symbol === symbol);
			if (item) item.in_watchlist = value;
		}
	}
});

export const { stocksRequested, stocksReceived, stocksFailed, setInWatchlist } = stocksSlice.actions;

// Returns { stock_list, first_symbol_order_book }. Reuses the cached list when it
// is fresher than MAX_AGE_MS unless force is set.
export const loadStocks =
	({ force = false } = {}) =>
	async (dispatch, getState) => {
		const current = getState().stocks;
		if (!force && current.lastFetched && Date.now() - current.lastFetched < MAX_AGE_MS && current.list.length) {
			return { stock_list: current.list, first_symbol_order_book: [], cached: true };
		}
		dispatch(stocksRequested());
		try {
			const res = await axios.get('/portfolio/stocks/');
			dispatch(stocksReceived(res.data.stock_list));
			writeCachedList(getState().stocks.list);
			return { stock_list: res.data.stock_list || [], first_symbol_order_book: res.data.first_symbol_order_book || [], cached: false };
		} catch (err) {
			dispatch(stocksFailed(err?.message));
			throw err;
		}
	};

export const selectStockList = state => state.stocks.list;

export default stocksSlice.reducer;
